/**
 * Contract Quick Actions Component
 * US-CONT-005: View Expiring Contracts Alert
 * US-CONT-006: Terminate Contract
 * Dropdown menu with quick actions for contracts
 */

import { useState } from 'react';
import { MoreVertical, RefreshCw, Eye, Mail, Download, Edit, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import type { ContractAction, ExpiringContract } from '../types/expiringContracts.types';
import { useContractActions } from '../hooks/useContractActions';
import { TerminateContractDialog } from '@/features/contracts/termination/components/TerminateContractDialog';
import { useTerminationWorkflow } from '@/features/contracts/termination/hooks/useContractTermination';

interface ContractQuickActionsProps {
  contract: ExpiringContract;
  onAction: (action: ContractAction) => void;
  isRenewing?: boolean;
  className?: string;
  onTerminationSuccess?: (contractId: string) => void;
}

export function ContractQuickActions({
  contract,
  onAction,
  isRenewing = false,
  className,
  onTerminationSuccess
}: ContractQuickActionsProps) {
  const { createAction, isActionAvailable, isActionLoading } = useContractActions();
  const {
    selectedContract,
    isDialogOpen,
    isTerminating,
    openTerminationDialog,
    closeTerminationDialog,
    handleTerminate
  } = useTerminationWorkflow(onTerminationSuccess);

  function handleActionClick(actionType: ContractAction['type']) {
    // Handle terminate action specially
    if (actionType === 'TERMINATE') {
      openTerminationDialog(contract);
      return;
    }

    const action = createAction(actionType, contract);
    onAction(action);
  }

  const isLoading = isRenewing || isActionLoading('RENEW') || isActionLoading('DOWNLOAD') || isTerminating;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn('h-8 w-8', className)}
          disabled={isLoading}
        >
          <MoreVertical className="h-4 w-4" />
          <span className="sr-only">Open actions menu</span>
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel>Actions</DropdownMenuLabel>
        <DropdownMenuSeparator />

        {/* View Details */}
        <DropdownMenuItem
          onClick={() => handleActionClick('VIEW_DETAILS')}
          className="cursor-pointer"
        >
          <Eye className="mr-2 h-4 w-4" />
          <span>View Details</span>
        </DropdownMenuItem>

        {/* Renew Contract */}
        {isActionAvailable('RENEW', contract) && (
          <DropdownMenuItem
            onClick={() => handleActionClick('RENEW')}
            disabled={isRenewing}
            className="cursor-pointer"
          >
            <RefreshCw className={cn('mr-2 h-4 w-4', isRenewing && 'animate-spin')} />
            <span>{isRenewing ? 'Renewing...' : 'Renew Contract'}</span>
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />

        {/* Contact Customer */}
        <DropdownMenuItem
          onClick={() => handleActionClick('CONTACT_CUSTOMER')}
          className="cursor-pointer"
        >
          <Mail className="mr-2 h-4 w-4" />
          <span>Contact Customer</span>
        </DropdownMenuItem>

        {/* Download Contract */}
        <DropdownMenuItem
          onClick={() => handleActionClick('DOWNLOAD')}
          className="cursor-pointer"
        >
          <Download className="mr-2 h-4 w-4" />
          <span>Download Contract</span>
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        {/* Edit Contract */}
        {isActionAvailable('EDIT', contract) && (
          <DropdownMenuItem
            onClick={() => handleActionClick('EDIT')}
            className="cursor-pointer"
          >
            <Edit className="mr-2 h-4 w-4" />
            <span>Edit Contract</span>
          </DropdownMenuItem>
        )}

        {/* Terminate Contract */}
        {isActionAvailable('TERMINATE', contract) && (
          <DropdownMenuItem
            onClick={() => handleActionClick('TERMINATE')}
            className="cursor-pointer text-destructive focus:text-destructive"
            disabled={isTerminating}
          >
            <XCircle className="mr-2 h-4 w-4" />
            <span>{isTerminating ? 'Terminating...' : 'Terminate Contract'}</span>
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>

      {/* Termination Dialog */}
      <TerminateContractDialog
        contract={selectedContract}
        open={isDialogOpen}
        onOpenChange={closeTerminationDialog}
        onConfirm={handleTerminate}
        isLoading={isTerminating}
      />
    </DropdownMenu>
  );
}

/**
 * Compact actions (for mobile or small screens)
 */
interface CompactActionsProps {
  contract: ExpiringContract;
  onRenew: () => void;
  onViewDetails: () => void;
  isRenewing?: boolean;
  className?: string;
}

export function CompactContractActions({
  contract,
  onRenew,
  onViewDetails,
  isRenewing = false,
  className
}: CompactActionsProps) {
  const { isActionAvailable } = useContractActions();

  return (
    <div className={cn('flex items-center gap-1', className)}>
      <Button
        variant="ghost"
        size="sm"
        onClick={onViewDetails}
        className="h-8"
      >
        <Eye className="mr-1 h-3 w-3" />
        View
      </Button>

      {isActionAvailable('RENEW', contract) && (
        <Button
          variant="default"
          size="sm"
          onClick={onRenew}
          disabled={isRenewing}
          className="h-8"
        >
          <RefreshCw className={cn('mr-1 h-3 w-3', isRenewing && 'animate-spin')} />
          {isRenewing ? 'Renewing...' : 'Renew'}
        </Button>
      )}
    </div>
  );
}
